import { useMemo } from 'react';

// porp-types is a library for typechecking of props
import PropTypes from 'prop-types';

// @mui/x-charts components
import { BarChart } from '@mui/x-charts/BarChart';

// @mui material components
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function ReportsBarChart({ color, title, description, date, chart }) {
    // Transform data for MUI X Charts
    const series = chart.datasets.map((dataset) => ({
        data: dataset.data,
        label: dataset.label,
        color: dataset.backgroundColor || color,
    }));

    // Create dataset for BarChart
    const chartData = [];
    if (chart.labels && chart.datasets.length > 0) {
        chart.labels.forEach((label, index) => {
            const dataPoint = { category: label };
            chart.datasets.forEach((dataset) => {
                const key = dataset.label.toLowerCase().replace(/\s+/g, '_');
                dataPoint[key] = dataset.data[index];
            });
            chartData.push(dataPoint);
        });
    }

    // Create series configuration
    const barChartSeries = chart.datasets.map((dataset) => {
        const key = dataset.label.toLowerCase().replace(/\s+/g, '_');
        return {
            dataKey: key,
            label: dataset.label,
            color: dataset.backgroundColor || color,
        };
    });

    return (
        <Card sx={{ height: '100%' }}>
            <MDBox padding="1rem">
                {useMemo(
                    () => (
                        <MDBox
                            variant="gradient"
                            bgColor={color}
                            borderRadius="lg"
                            coloredShadow={color}
                            py={2}
                            pr={0.5}
                            mt={-5}
                            height="12.5rem"
                        >
                            <BarChart
                                dataset={chartData}
                                xAxis={[
                                    {
                                        scaleType: 'band',
                                        dataKey: 'category',
                                        tickLabelStyle: {
                                            color: 'white',
                                            fontSize: 10,
                                        },
                                    },
                                ]}
                                series={barChartSeries}
                                height={220}
                                slotProps={{
                                    legend: {
                                        hidden: true,
                                    },
                                }}
                                sx={{
                                    '.MuiBarElement-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                            />
                        </MDBox>
                    ),
                    [chart, color]
                )}
                <MDBox pt={3} pb={1} px={1}>
                    <MDTypography variant="h6" textTransform="capitalize">
                        {title}
                    </MDTypography>
                    <MDTypography component="div" variant="button" color="text" fontWeight="light">
                        {description}
                    </MDTypography>
                    <Divider />
                    <MDBox display="flex" alignItems="center">
                        <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
                            <Icon>schedule</Icon>
                        </MDTypography>
                        <MDTypography variant="button" color="text" fontWeight="light">
                            {date}
                        </MDTypography>
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
}

// Setting default values for the props of ReportsBarChart
ReportsBarChart.defaultProps = {
    color: 'dark',
    description: '',
};

// Typechecking props for the ReportsBarChart
ReportsBarChart.propTypes = {
    color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
    title: PropTypes.string.isRequired,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    date: PropTypes.string.isRequired,
    chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
};

export default ReportsBarChart;
